const ENABLED_STORAGE_KEY = "ea.routeProfiler.enabled";
const TRACE_STORAGE_KEY = "ea.routeProfiler.traceId";
const RECORDS_STORAGE_KEY = "ea.routeProfiler.records";
const MAX_RECORDS = 100;
const TRUE_VALUES = new Set(["1", "true", "yes", "on"]);
const FALSE_VALUES = new Set(["0", "false", "no", "off"]);

const roundMs = (value) => {
  if (!Number.isFinite(value)) return null;
  return Math.round(value);
};

const safeUrl = (value) => {
  try {
    return new URL(value, "https://app.eye.academy/");
  } catch (_error) {
    return null;
  }
};

const normalizeFlag = (value) => {
  if (value === true) return true;
  if (value === false || value == null) return false;
  const normalized = String(value).trim().toLowerCase();
  if (TRUE_VALUES.has(normalized)) return true;
  if (FALSE_VALUES.has(normalized)) return false;
  return false;
};

const readStorageValue = (storage, key) => {
  if (!storage || typeof storage.getItem !== "function") return null;
  try {
    return storage.getItem(key);
  } catch (_error) {
    return null;
  }
};

const writeStorageValue = (storage, key, value) => {
  if (!storage || typeof storage.setItem !== "function") return;
  try {
    storage.setItem(key, value);
  } catch (_error) {
    // Profiling must never break the app.
  }
};

export const shouldEnableRouteProfiler = ({
  search = "",
  storageValue = null,
  localStorage = null,
} = {}) => {
  const params = new URLSearchParams(search || "");
  for (const key of ["eaProfile", "profileRoutes", "routeProfile"]) {
    if (params.has(key)) {
      return normalizeFlag(params.get(key));
    }
  }

  return normalizeFlag(
    storageValue ?? readStorageValue(localStorage, ENABLED_STORAGE_KEY),
  );
};

export const buildProfilerUrl = (url, traceId) => {
  const parsed = safeUrl(url);
  if (!parsed) return url;
  parsed.searchParams.set("eaProfile", "1");
  if (traceId) {
    parsed.searchParams.set("eaProfileTrace", traceId);
  }
  return parsed.toString();
};

export const percentile = (values, percentileValue) => {
  const numeric = values
    .filter((value) => Number.isFinite(value))
    .sort((left, right) => left - right);
  if (numeric.length === 0) return null;
  const rank = Math.ceil((percentileValue / 100) * numeric.length) - 1;
  return numeric[Math.max(0, Math.min(numeric.length - 1, rank))];
};

const summarizeEntry = (entry) => {
  const parsed = safeUrl(entry.name);
  return {
    name: entry.name,
    host: parsed?.host || "",
    path: parsed?.pathname || "",
    initiatorType: entry.initiatorType || "unknown",
    durationRawMs: Number.isFinite(entry.duration) ? entry.duration : 0,
    durationMs: roundMs(entry.duration) || 0,
    transferSize: Number.isFinite(entry.transferSize)
      ? Math.max(0, Math.round(entry.transferSize))
      : 0,
  };
};

export const summarizeResourceTimings = (entries, { topLimit = 12 } = {}) => {
  const resources = Array.isArray(entries) ? entries.map(summarizeEntry) : [];
  const byInitiator = {};

  resources.forEach((entry) => {
    const key = entry.initiatorType || "unknown";
    if (!byInitiator[key]) {
      byInitiator[key] = {
        count: 0,
        durationMs: 0,
        transferSize: 0,
      };
    }
    byInitiator[key].count += 1;
    byInitiator[key].durationMs += entry.durationRawMs;
    byInitiator[key].transferSize += entry.transferSize;
  });

  Object.values(byInitiator).forEach((entry) => {
    entry.durationMs = roundMs(entry.durationMs) || 0;
  });

  return {
    totalCount: resources.length,
    byInitiator,
    top: [...resources]
      .sort((left, right) => right.durationMs - left.durationMs)
      .slice(0, topLimit)
      .map(({ durationRawMs, ...entry }) => entry),
  };
};

const getTraceId = (windowLike) => {
  const params = new URLSearchParams(windowLike?.location?.search || "");
  const explicitTrace = params.get("eaProfileTrace");
  if (explicitTrace) {
    writeStorageValue(windowLike.localStorage, TRACE_STORAGE_KEY, explicitTrace);
    return explicitTrace;
  }

  const stored = readStorageValue(windowLike?.localStorage, TRACE_STORAGE_KEY);
  if (stored) return stored;

  const generated = `route-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
  writeStorageValue(windowLike?.localStorage, TRACE_STORAGE_KEY, generated);
  return generated;
};

const collectResourceEntries = (performanceLike, startTime = 0) => {
  if (!performanceLike?.getEntriesByType) return [];
  try {
    return performanceLike
      .getEntriesByType("resource")
      .filter((entry) => entry.startTime >= startTime);
  } catch (_error) {
    return [];
  }
};

const collectNavigationEntry = (performanceLike) => {
  if (!performanceLike?.getEntriesByType) return null;
  try {
    const [entry] = performanceLike.getEntriesByType("navigation");
    if (!entry) return null;
    return {
      type: entry.type,
      domContentLoadedMs: roundMs(entry.domContentLoadedEventEnd),
      loadEventMs: roundMs(entry.loadEventEnd),
      transferSize: Number.isFinite(entry.transferSize)
        ? Math.round(entry.transferSize)
        : null,
    };
  } catch (_error) {
    return null;
  }
};

export const getActivePerfTrace = (windowLike = globalThis.window) => {
  const profiler = windowLike?.__EA_ROUTE_PROFILER__;
  if (profiler?.enabled) {
    return {
      traceId: profiler.traceId,
      route: windowLike?.location?.pathname || "",
      sampleId: profiler.currentSampleId || "",
    };
  }

  const telemetry = windowLike?.__EA_PERFORMANCE_TELEMETRY__;
  if (!telemetry?.enabled) return null;
  return {
    traceId: telemetry.traceId,
    route: telemetry.routePath || windowLike?.location?.pathname || "",
    sampleId: telemetry.currentSampleId || "",
  };
};

export const withPerfTrace = (payload, windowLike = globalThis.window) => {
  const trace = getActivePerfTrace(windowLike);
  if (!trace?.traceId || payload == null || typeof payload !== "object") {
    return payload;
  }
  return {
    ...payload,
    __perfTrace: trace,
  };
};

export const installRouteProfiler = ({
  router,
  window: windowLike = globalThis.window,
  console: consoleLike = globalThis.console,
} = {}) => {
  if (!router || !windowLike?.performance) return null;

  const enabled = shouldEnableRouteProfiler({
    search: windowLike.location?.search || "",
    localStorage: windowLike.localStorage,
  });
  if (!enabled) return null;

  writeStorageValue(windowLike.localStorage, ENABLED_STORAGE_KEY, "true");

  const performanceLike = windowLike.performance;
  const traceId = getTraceId(windowLike);
  const records = [];
  const longTasks = [];
  let current = null;
  let observer = null;

  if (typeof windowLike.PerformanceObserver === "function") {
    try {
      observer = new windowLike.PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          longTasks.push({
            startTime: roundMs(entry.startTime),
            durationMs: roundMs(entry.duration),
            name: entry.name || "longtask",
          });
        });
      });
      observer.observe({ entryTypes: ["longtask"] });
    } catch (_error) {
      observer = null;
    }
  }

  const persistRecords = () => {
    writeStorageValue(
      windowLike.sessionStorage,
      RECORDS_STORAGE_KEY,
      JSON.stringify(records.slice(-MAX_RECORDS)),
    );
  };

  const record = (entry) => {
    records.push(entry);
    while (records.length > MAX_RECORDS) records.shift();
    persistRecords();
    if (consoleLike?.info) {
      consoleLike.info("[EA route profile]", entry);
    }
    return entry;
  };

  const mark = (name, data = {}) => {
    if (!current) return null;
    const now = performanceLike.now();
    const entry = {
      name,
      atMs: roundMs(now - current.startedAt),
      ...data,
    };
    current.marks.push(entry);
    return entry;
  };

  const finishCurrentRoute = (status = "ready", data = {}) => {
    if (!current) return null;
    const endedAt = performanceLike.now();
    const startedAt = current.startedAt;
    const routeLongTasks = longTasks.filter(
      (entry) => entry.startTime >= roundMs(startedAt),
    );
    const entry = record({
      event: "ea_route_profile",
      traceId,
      sampleId: current.sampleId,
      status,
      from: current.from,
      to: current.to,
      routeName: current.routeName,
      durationMs: roundMs(endedAt - startedAt),
      marks: current.marks,
      navigation: collectNavigationEntry(performanceLike),
      resources: summarizeResourceTimings(
        collectResourceEntries(performanceLike, current.resourceStartTime),
      ),
      longTasks: routeLongTasks,
      ...data,
    });
    current = null;
    return entry;
  };

  const api = {
    enabled: true,
    traceId,
    get currentSampleId() {
      return current?.sampleId || "";
    },
    records,
    mark,
    finishCurrentRoute,
    record,
    stop() {
      if (observer) observer.disconnect();
      observer = null;
    },
  };

  windowLike.__EA_ROUTE_PROFILER__ = api;

  router.beforeEach((to, from) => {
    if (current) {
      finishCurrentRoute("interrupted");
    }
    current = {
      sampleId: `${Date.now().toString(36)}-${Math.random()
        .toString(36)
        .slice(2, 8)}`,
      startedAt: performanceLike.now(),
      resourceStartTime: performanceLike.now(),
      from: from?.fullPath || "",
      to: to?.fullPath || "",
      routeName: to?.name || "",
      marks: [],
    };
    mark("beforeEach");
    return true;
  });

  router.beforeResolve(() => {
    mark("beforeResolve");
    return true;
  });

  router.afterEach(() => {
    mark("afterEach");
    windowLike.requestAnimationFrame?.(() => {
      windowLike.requestAnimationFrame?.(() => {
        windowLike.setTimeout?.(() => {
          if (current) {
            finishCurrentRoute("after-paint");
          }
        }, 250);
      });
    });
  });

  router.onError((error) => {
    finishCurrentRoute("error", {
      error: error?.message || String(error),
    });
  });

  if (consoleLike?.info) {
    consoleLike.info("[EA route profile] enabled", { traceId });
  }

  return api;
};
